import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError, useAuth } from "@/context/AuthContext";
import DetailedCriteriaPanel from "@/components/training/DetailedCriteriaPanel";
import TaskVisualCard from "@/components/training/TaskVisualCard";
import { visualKindForDetailedCategory } from "@/training/taskVisuals";
import SketchModal from "@/components/sketch/SketchModal";
import { SKETCH_TEMPLATE_LABELS } from "@/components/sketch/SketchTemplateUnderlay";
import QuickDailyFeedbackPanel, {
  assessmentsFromState,
  computeProgressScore,
  customAssessmentsFromState,
  emptyCustomCategoryState,
  emptyTaskState,
  stateFromAssessments,
  stateFromCustomAssessments,
} from "@/components/training/QuickDailyFeedbackPanel";
import InstructorCustomCategoriesPanel from "@/components/instructor/InstructorCustomCategoriesPanel";
import InstructorLearnerHeader from "@/components/instructor/InstructorLearnerHeader";
import InstructorLearnerPicker from "@/components/instructor/InstructorLearnerPicker";
import InstructorFocusToday from "@/components/instructor/InstructorFocusToday";
import PerformanceDashboardPanel from "@/components/instructor/PerformanceDashboardPanel";
import InstructorActionBar from "@/components/instructor/InstructorActionBar";
import TrainingTimeline from "@/components/instructor/TrainingTimeline";
import InstructorModeTabs from "@/components/instructor/InstructorModeTabs";
import InstructorAdvancedAccess from "@/components/instructor/InstructorAdvancedAccess";
import InstructorPracticalTrainingWrites from "@/components/instructor/InstructorPracticalTrainingWrites";
import InstructorTrainingOutcome from "@/components/instructor/InstructorTrainingOutcome";
import InstructorPracticeHoursPanel from "@/components/instructor/InstructorPracticeHoursPanel";
import { nzLocalDateString } from "@/lib/nzDate";
import { qdfFormHasMeaningfulContent } from "@/lib/draftContent";
import { createDraftSaveCoordinator } from "@/lib/draftSaveCoordinator";
import { consumeGatewayLaunchContext } from "@/lib/loginResume";

function pendingClientId() {
  return `pending-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function isInstructorSession(user) {
  return user && (user.role === "instructor" || user.role === "admin");
}

const DEFAULT_PROGRAMME = "programme-nz-road-code";
const PROGRAMME_LABELS = {
  "programme-nz-road-code": "NZ Road Code",
};
const QUICK_PICKS = [
  "ql-mirrors",
  "ql-lane-position",
  "ql-roundabouts",
  "ql-gap-selection",
  "ql-parallel-parking",
];
/** Local labels so Quick picks never flash raw task IDs if catalogue is slow. */
const QUICK_TASK_LABELS = {
  "ql-mirrors": "Mirrors",
  "ql-lane-position": "Lane position",
  "ql-roundabouts": "Roundabouts",
  "ql-gap-selection": "Gap selection",
  "ql-parallel-parking": "Parallel parking",
};

function nowAmPm() {
  const h = new Date().getHours();
  return h < 12 ? "AM" : "PM";
}

function programmeLabel(programmeId) {
  return PROGRAMME_LABELS[programmeId] || "Assigned programme";
}

function isPracticalTrainingEnabled(learner) {
  if (!learner) return false;
  if (typeof learner.practical_access_enabled === "boolean") {
    return learner.practical_access_enabled;
  }
  const ent = (learner.learner_entitlement || learner.training_entitlement || "").toUpperCase();
  return ent === "TRAINING_ENABLED" || ent === "TRAINING_FULL" || ent === "ORG_SPONSORED";
}

function programmeForLearner(assignments, learnerId) {
  const mine = (assignments || []).filter((a) => a.learner_id === learnerId);
  if (!mine.length) return DEFAULT_PROGRAMME;
  const preferred = mine.find((a) => a.programme_id === DEFAULT_PROGRAMME);
  return (preferred || mine[0]).programme_id;
}

function compareSketchesDesc(a, b) {
  const c = String(b.updated_at || b.created_at || "").localeCompare(
    String(a.updated_at || a.created_at || "")
  );
  if (c !== 0) return c;
  return String(b.sketch_id).localeCompare(String(a.sketch_id));
}

function mergeSketchLists(prev, incoming) {
  const map = new Map();
  [...prev, ...incoming].forEach((s) => map.set(s.sketch_id, s));
  return [...map.values()].sort(compareSketchesDesc);
}

function emptyForm(overrides = {}) {
  return {
    learner_id: "",
    programme_id: DEFAULT_PROGRAMME,
    training_mode: "A",
    workflow_kind: "QUICK_DAILY_FEEDBACK",
    score_visibility: "HIDDEN",
    external_share_state: "NOT_SHAREABLE_EXTERNALLY",
    session_date: nzLocalDateString(),
    session_time_ampm: nowAmPm(),
    confirmed_driving_minutes: "",
    strengths: "",
    areas_for_improvement: "",
    instructor_notes_private: "",
    next_session_focus: "",
    readiness_state: "DEVELOPING",
    hazard_note: "",
    ...overrides,
  };
}

export default function InstructorDashboard() {
  const { authReady, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const asInstructorId = searchParams.get("as_instructor_id") || "";
  const isAdminViewer = user?.role === "admin";
  const [adminInstructors, setAdminInstructors] = useState([]);
  const formSectionRef = useRef(null);
  const qaDetailsRef = useRef(null);
  const autosaveTimerRef = useRef(null);
  const publishGuardRef = useRef(false);
  const draftSaveCoordinatorRef = useRef(createDraftSaveCoordinator());
  const draftRestorePendingRef = useRef(false);
  const [learners, setLearners] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [selectedLearner, setSelectedLearner] = useState(null);
  const [history, setHistory] = useState([]);
  const [quickCategories, setQuickCategories] = useState([]);
  const [dailyTasks, setDailyTasks] = useState([]);
  const [dailyScoreLabels, setDailyScoreLabels] = useState({});
  const [taskState, setTaskState] = useState({});
  const [customCategories, setCustomCategories] = useState([]);
  const [customCategoryState, setCustomCategoryState] = useState({});
  const [autosaveStatus, setAutosaveStatus] = useState("idle");
  const [formDirty, setFormDirty] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [detailedCategories, setDetailedCategories] = useState([]);
  const [openDetailed, setOpenDetailed] = useState({});
  const [selectedTasks, setSelectedTasks] = useState([]);
  const [selectedSkills, setSelectedSkills] = useState({}); // skill_id -> score "" | 1-5
  const [tab, setTab] = useState("QUICK_DAILY_FEEDBACK");
  const [form, setForm] = useState(emptyForm());
  const [aiDraft, setAiDraft] = useState(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [gatewayError, setGatewayError] = useState("");
  const [learnersStatus, setLearnersStatus] = useState("idle");
  const [learnersError, setLearnersError] = useState("");
  const [timingLog, setTimingLog] = useState([]);

  // Sprint 4 — Driving Sketch Pad
  const [currentRecordId, setCurrentRecordId] = useState(null);
  const [sketches, setSketches] = useState([]);
  const [sketchesTruncated, setSketchesTruncated] = useState(false);
  const [sketchListCursor, setSketchListCursor] = useState(null);
  const [sketchesLoadingMore, setSketchesLoadingMore] = useState(false);
  const [learnerProgression, setLearnerProgression] = useState(null);
  const [pendingSketchIds, setPendingSketchIds] = useState([]);
  const [sketchModalOpen, setSketchModalOpen] = useState(false);
  const [editingSketch, setEditingSketch] = useState(null);
  const [sketchSaving, setSketchSaving] = useState(false);
  const [sketchError, setSketchError] = useState("");
  const [sketchViewReadOnly, setSketchViewReadOnly] = useState(false);
  const [timelineRefreshKey, setTimelineRefreshKey] = useState(0);
  /** Inline QDF sketchbook: update-in-place instead of creating duplicates on each save. */
  const [inlineSketchId, setInlineSketchId] = useState(null);
  const [sketchSaveRevision, setSketchSaveRevision] = useState(null);
  const [makeLatestSketchVisible, setMakeLatestSketchVisible] = useState(false);

  // Sprint 4 — Learning Preferences (compact, non-diagnostic instructor view)
  const [learnerPreferences, setLearnerPreferences] = useState(null);
  const [rosterListCounts, setRosterListCounts] = useState(null);
  const [operationalLifecycleTabCounts, setOperationalLifecycleTabCounts] = useState(null);
  const [operationalMirrorScheduler, setOperationalMirrorScheduler] = useState(null);
  const [rosterActionBusy, setRosterActionBusy] = useState(false);
  const prevAsInstructorIdRef = useRef(undefined);

  const learnerOnWorkingRoster = useCallback(
    (learnerId) => Boolean(learnerId && learners.some((l) => l.id === learnerId)),
    [learners]
  );

  useEffect(() => {
    if (!selectedLearner?.id) return;
    const fresh = learners.find((l) => l.id === selectedLearner.id);
    if (fresh && fresh !== selectedLearner) {
      setSelectedLearner(fresh);
    }
  }, [learners, selectedLearner]);

  const clearLearnerWorkflowState = useCallback(() => {
    setSelectedLearner(null);
    setForm(emptyForm());
    setHistory([]);
    setTaskState(emptyTaskState(dailyTasks));
    setCustomCategoryState({});
    setCurrentRecordId(null);
    setSketches([]);
    setPendingSketchIds([]);
    setMakeLatestSketchVisible(false);
    setLearnerProgression(null);
    setLearnerPreferences(null);
    setFormDirty(false);
    setAutosaveStatus("idle");
    setMessage("");
    setError("");
    setAiDraft(null);
  }, [dailyTasks]);

  const loadLearners = useCallback(() => {
    if (isAdminViewer && !asInstructorId) {
      setLearnersStatus("ready");
      setLearners([]);
      setAssignments([]);
      return Promise.resolve();
    }
    setLearnersStatus("loading");
    setLearnersError("");
    const params = isAdminViewer && asInstructorId ? { as_instructor_id: asInstructorId } : undefined;
    return api
      .get("/instructor/learners", { params })
      .then((res) => {
        setLearners(res.data.learners || []);
        setAssignments(res.data.assignments || []);
        setRosterListCounts(res.data.roster_list_counts || null);
        setOperationalLifecycleTabCounts(
          res.data.operational_lifecycle_tab_counts || res.data.lifecycle_view_counts || null
        );
        setOperationalMirrorScheduler(res.data.operational_mirror_scheduler || null);
        setLearnersStatus("ready");
      })
      .catch((err) => {
        setLearnersError(formatApiError(err) || "Could not load learners.");
        setLearnersStatus("error");
      });
  }, [asInstructorId, isAdminViewer]);

  useEffect(() => {
    if (!isAdminViewer || !asInstructorId) return undefined;
    const interceptorId = api.interceptors.request.use((config) => {
      const rawUrl = config.url || "";
      const needsActing =
        rawUrl.includes("/instructor/") || rawUrl.includes("/access-workflow/instructor");
      if (!needsActing) return config;
      const params = config.params && typeof config.params === "object" ? { ...config.params } : {};
      if (!params.as_instructor_id) params.as_instructor_id = asInstructorId;
      config.params = params;
      return config;
    });
    return () => {
      api.interceptors.request.eject(interceptorId);
    };
  }, [isAdminViewer, asInstructorId]);

  useEffect(() => {
    if (!isAdminViewer) return;
    const prev = prevAsInstructorIdRef.current;
    prevAsInstructorIdRef.current = asInstructorId;
    if (prev === undefined || prev === asInstructorId) return;
    clearLearnerWorkflowState();
    setPickerOpen(false);
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete("learner_id");
        if (asInstructorId) next.set("as_instructor_id", asInstructorId);
        else next.delete("as_instructor_id");
        return next;
      },
      { replace: true }
    );
  }, [asInstructorId, clearLearnerWorkflowState, isAdminViewer, setSearchParams]);

  useEffect(() => {
    if (!isAdminViewer) return;
    api
      .get("/admin/instructors")
      .then((res) => setAdminInstructors(res.data.instructors || []))
      .catch(() => {});
  }, [isAdminViewer]);

  const archiveLearnerOnRoster = useCallback(
    async (learner) => {
      if (!learner?.id) return;
      setRosterActionBusy(true);
      try {
        await api.post(`/instructor/learners/${learner.id}/roster-archive`);
        await loadLearners();
      } catch (err) {
        setLearnersError(formatApiError(err) || "Could not archive learner.");
      } finally {
        setRosterActionBusy(false);
      }
    },
    [loadLearners]
  );

  const restoreLearnerOnRoster = useCallback(
    async (learner) => {
      if (!learner?.id) return;
      setRosterActionBusy(true);
      try {
        await api.post(`/instructor/learners/${learner.id}/roster-restore`);
        await loadLearners();
      } catch (err) {
        setLearnersError(formatApiError(err) || "Could not restore learner.");
      } finally {
        setRosterActionBusy(false);
      }
    },
    [loadLearners]
  );

  const loadCustomCategories = () =>
    api
      .get("/instructor/custom-categories")
      .then((res) => {
        const cats = res.data.categories || [];
        setCustomCategories(cats);
        setCustomCategoryState((prev) => {
          const active = cats.filter((c) => c.active && !c.archived);
          const base = emptyCustomCategoryState(active);
          for (const c of active) {
            if (prev[c.id]) base[c.id] = prev[c.id];
          }
          return base;
        });
      })
      .catch(() => {});

  useEffect(() => {
    if (!authReady) return;
    loadLearners();
    loadCustomCategories();
    api
      .get("/training/quick-lesson-catalogue")
      .then((res) => {
        setQuickCategories(res.data.quick_lesson_categories || []);
        setDetailedCategories(res.data.detailed_categories || []);
        const tasks = res.data.daily_feedback_tasks || [];
        setDailyTasks(tasks);
        setDailyScoreLabels(res.data.daily_score_labels || {});
        setTaskState(emptyTaskState(tasks));
      })
      .catch(() => {});
  }, [authReady, loadLearners]);

  useEffect(() => {
    if (searchParams.get("launch") !== "gdi") return;
    const ctx = consumeGatewayLaunchContext();
    if (!ctx) return;
    setGatewayError("");
    (async () => {
      try {
        const { data } = await api.post("/gateway/finalize-launch", { launch_context: ctx });
        const gwParams = isAdminViewer && asInstructorId ? { as_instructor_id: asInstructorId } : undefined;
        const res = await api.get("/instructor/learners", { params: gwParams });
        const loaded = res.data.learners || [];
        setLearners(loaded);
        setAssignments(res.data.assignments || []);
        setRosterListCounts(res.data.roster_list_counts || null);
        setOperationalLifecycleTabCounts(
          res.data.operational_lifecycle_tab_counts || res.data.lifecycle_view_counts || null
        );
        setOperationalMirrorScheduler(res.data.operational_mirror_scheduler || null);
        setLearnersStatus("ready");
        setLearnersError("");
        if (data?.academy_learner_id) {
          const target = loaded.find((l) => l.id === data.academy_learner_id);
          if (target) {
            selectLearner(target);
            setPickerOpen(data.learner_selection === "picker");
          } else if (data.learner_selection === "picker") {
            setPickerOpen(true);
          }
        }
      } catch (err) {
        setGatewayError(formatApiError(err) || "GDI training launch could not be completed.");
        setLearnersError(formatApiError(err) || "Could not load learners.");
        setLearnersStatus("error");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  useEffect(() => {
    if (learnersStatus !== "ready" || selectedLearner || searchParams.get("learner_id")) return;
    if (searchParams.get("launch") === "gdi") return;
    if (learners.length === 1) {
      selectLearner(learners[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [learnersStatus, learners, selectedLearner, searchParams]);

  const activeCustomCategories = useMemo(
    () => (customCategories || []).filter((c) => c.active && !c.archived),
    [customCategories]
  );

  const handleCategoriesChange = (cats) => {
    setCustomCategories(cats);
    setCustomCategoryState((prev) => {
      const active = (cats || []).filter((c) => c.active && !c.archived);
      const base = emptyCustomCategoryState(active);
      for (const c of active) {
        if (prev[c.id]) base[c.id] = prev[c.id];
      }
      return base;
    });
  };

  const hydrateInlineSketchSelection = (loaded) => {
    setSketches(loaded);
    setInlineSketchId((prev) => {
      if (prev && loaded.some((s) => s.sketch_id === prev)) return prev;
      if (!loaded.length) return null;
      const latest = [...loaded].sort((a, b) =>
        String(b.updated_at || b.created_at || "").localeCompare(String(a.updated_at || a.created_at || ""))
      )[0];
      return latest?.sketch_id || null;
    });
  };

  const loadLearnerSketches = async (learnerId, programmeId, { append = false, cursor = null } = {}) => {
    if (!learnerId || !programmeId) {
      setSketches([]);
      setInlineSketchId(null);
      setSketchSaveRevision(null);
      setSketchesTruncated(false);
      setSketchListCursor(null);
      return;
    }
    try {
      const params = { learner_id: learnerId, programme_id: programmeId };
      if (cursor?.before_updated_at) {
        params.before_updated_at = cursor.before_updated_at;
        if (cursor.before_sketch_id) params.before_sketch_id = cursor.before_sketch_id;
      }
      const res = await api.get("/instructor/training-sketches", { params });
      const incoming = res.data.sketches || [];
      if (append) {
        setSketches((prev) => mergeSketchLists(prev, incoming));
      } else {
        hydrateInlineSketchSelection(incoming);
      }
      setSketchesTruncated(!!res.data.truncated);
      if (res.data.truncated) {
        setSketchListCursor({
          before_updated_at: res.data.next_before_updated_at,
          before_sketch_id: res.data.next_before_sketch_id,
        });
      } else if (!append) {
        setSketchListCursor(null);
      }
    } catch {
      if (!append) {
        setSketches([]);
        setInlineSketchId(null);
        setSketchSaveRevision(null);
      }
      setSketchesTruncated(false);
      if (!append) setSketchListCursor(null);
    }
  };

  const loadMoreLearnerSketches = async () => {
    if (!sketchListCursor || !form.learner_id || !form.programme_id || sketchesLoadingMore) return;
    setSketchesLoadingMore(true);
    try {
      await loadLearnerSketches(form.learner_id, form.programme_id, {
        append: true,
        cursor: sketchListCursor,
      });
    } finally {
      setSketchesLoadingMore(false);
    }
  };

  const loadSketchesForRecord = async (recordId) => {
    if (!recordId) {
      await loadLearnerSketches(form.learner_id, form.programme_id);
      return;
    }
    try {
      const res = await api.get(`/instructor/training-records/${recordId}/sketches`);
      hydrateInlineSketchSelection(res.data.sketches || []);
    } catch {
      await loadLearnerSketches(form.learner_id, form.programme_id);
    }
  };

  const inlineSketch = useMemo(
    () => sketches.find((s) => s.sketch_id === inlineSketchId) || null,
    [sketches, inlineSketchId]
  );

  const restoreDraftFromHistory = (records, learner, programmeId) => {
    const today = nzLocalDateString();
    const cachedId = sessionStorage.getItem(`qdf-draft-${learner.id}-${today}`);
    const draft =
      (cachedId && records.find((r) => r.id === cachedId && r.publication_state === "DRAFT")) ||
      [...records]
        .reverse()
        .find(
          (r) =>
            r.workflow_kind === "QUICK_DAILY_FEEDBACK" &&
            r.publication_state === "DRAFT" &&
            r.session_date === today
        );
    if (!draft) {
      setCurrentRecordId(null);
      setTaskState(emptyTaskState(dailyTasks));
      setCustomCategoryState(emptyCustomCategoryState(activeCustomCategories));
      draftRestorePendingRef.current = false;
      return;
    }
    setCurrentRecordId(draft.id);
    setTab("QUICK_DAILY_FEEDBACK");
    setForm((f) => ({
      ...f,
      learner_id: learner.id,
      programme_id: programmeId,
      workflow_kind: "QUICK_DAILY_FEEDBACK",
      training_mode: "A",
      session_date: draft.session_date || today,
      session_time_ampm: draft.session_time_ampm || nowAmPm(),
      strengths: draft.strengths || "",
      areas_for_improvement: draft.areas_for_improvement || "",
      next_session_focus: draft.next_session_focus || "",
      instructor_notes_private: draft.instructor_notes_private || "",
      score_visibility: draft.score_visibility || "HIDDEN",
    }));
    setTaskState(stateFromAssessments(draft.daily_task_assessments, dailyTasks));
    setCustomCategoryState(
      stateFromCustomAssessments(draft.custom_category_assessments, activeCustomCategories)
    );
    loadSketchesForRecord(draft.id);
    draftRestorePendingRef.current = false;
  };

  const loadHistory = async (learnerId, programmeId, learnerForRestore) => {
    if (!learnerId) return;
    try {
      const res = await api.get(`/instructor/learners/${learnerId}/history`, {
        params: { programme_id: programmeId || form.programme_id },
      });
      const records = res.data.records || [];
      setHistory(records);
      if (learnerForRestore) {
        restoreDraftFromHistory(records, learnerForRestore, programmeId);
      }
      setError("");
    } catch (err) {
      setError(err?.response?.data?.detail || "Not authorised for this learner/programme, or history unavailable.");
      setHistory([]);
      draftRestorePendingRef.current = false;
    }
    try {
      const prog = await api.get(`/instructor/learners/${learnerId}/training-progression`, {
        params: { programme_id: programmeId || form.programme_id },
      });
      setLearnerProgression(prog.data);
    } catch {
      setLearnerProgression(null);
    }
  };

  const selectLearner = (learner) => {
    if (selectedLearner?.id === learner.id) {
      setPickerOpen(false);
      return;
    }
    const programmeId = programmeForLearner(assignments, learner.id);
    setSelectedLearner(learner);
    setMessage("");
    setError("");
    setTab("QUICK_DAILY_FEEDBACK");
    setForm((f) => ({
      ...f,
      learner_id: learner.id,
      programme_id: programmeId,
      workflow_kind: "QUICK_DAILY_FEEDBACK",
      training_mode: "A",
      session_date: nzLocalDateString(),
      session_time_ampm: nowAmPm(),
      strengths: "",
      areas_for_improvement: "",
      next_session_focus: "",
      instructor_notes_private: "",
      score_visibility: "HIDDEN",
    }));
    setTaskState(emptyTaskState(dailyTasks));
    setCustomCategoryState(emptyCustomCategoryState(activeCustomCategories));
    setCurrentRecordId(null);
    draftRestorePendingRef.current = true;
    setSketches([]);
    setSketchesTruncated(false);
    setSketchListCursor(null);
    setPendingSketchIds([]);
    setMakeLatestSketchVisible(false);
    setFormDirty(false);
    setPickerOpen(false);
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.set("learner_id", learner.id);
        return next;
      },
      { replace: true }
    );
    setTimingLog((t) => [...t, { step: "select_learner", at: Date.now() }]);
    loadHistory(learner.id, programmeId, learner);
    loadLearnerSketches(learner.id, programmeId);
    loadLearnerPreferences(learner.id);
    window.requestAnimationFrame(() => {
      formSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  };

  useEffect(() => {
    if (searchParams.get("launch") === "gdi") return;
    const lid = searchParams.get("learner_id");
    const wf = searchParams.get("workflow");
    const focus = searchParams.get("focus");
    if (wf && ["QUICK_DAILY_FEEDBACK", "DETAILED_ASSESSMENT", "MOCK_READINESS", "QUICK_LESSON"].includes(wf)) {
      setTab(wf);
      setForm((f) => ({
        ...f,
        workflow_kind: wf,
        training_mode: wf === "MOCK_READINESS" ? "C" : wf === "DETAILED_ASSESSMENT" ? "B" : "A",
      }));
    }
    if (!lid || !learners.length) return;
    if (selectedLearner?.id === lid) return;
    const found = learners.find((l) => l.id === lid);
    if (found) selectLearner(found);
    if (focus === "history") {
      window.requestAnimationFrame(() => {
        document.querySelector('[data-testid="training-timeline"]')?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [learners, searchParams]);

  const loadLearnerPreferences = (learnerId) => {
    api
      .get(`/instructor/learners/${learnerId}/learning-preferences`)
      .then((res) => setLearnerPreferences(res.data))
      .catch(() => setLearnerPreferences(null));
  };

  const setWorkflowTab = (kind) => {
    setTab(kind);
    const mode = kind === "MOCK_READINESS" ? "C" : kind === "DETAILED_ASSESSMENT" ? "B" : "A";
    setForm((f) => ({ ...f, workflow_kind: kind, training_mode: mode }));
    setTimingLog((t) => [...t, { step: `tab_${kind}`, at: Date.now() }]);
  };

  const toggleTask = (taskId) => {
    setFormDirty(true);
    setSelectedTasks((prev) => {
      const next = prev.includes(taskId) ? prev.filter((id) => id !== taskId) : [...prev, taskId];
      return next;
    });
    setTimingLog((t) => [...t, { step: `task_${taskId}`, at: Date.now() }]);
  };

  const toggleSkill = (skillId) => {
    setFormDirty(true);
    setSelectedSkills((prev) => {
      const next = { ...prev };
      if (skillId in next) delete next[skillId];
      else next[skillId] = "";
      return next;
    });
  };

  const setSkillScore = (skillId, value) => {
    setFormDirty(true);
    setSelectedSkills((prev) => ({ ...prev, [skillId]: value }));
  };

  const requestSelectLearner = (learner) => {
    if (
      selectedLearner?.id &&
      learner.id !== selectedLearner.id &&
      (formDirty || autosaveStatus === "retry" || autosaveStatus === "saving")
    ) {
      if (!window.confirm("You have unsaved changes. Switch learner anyway? Unsaved work may be lost.")) {
        return;
      }
    }
    selectLearner(learner);
  };

  const handleTaskStateChange = (updater) => {
    setFormDirty(true);
    setTaskState(updater);
  };

  const handleCustomCategoryStateChange = (updater) => {
    setFormDirty(true);
    setCustomCategoryState(updater);
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    if (name === "learner_id" || name === "programme_id") return;
    setFormDirty(true);
    setForm((f) => ({ ...f, [name]: value }));
  };

  const buildPayload = () => {
    const assessments = Object.entries(selectedSkills).map(([competency_id, score]) => ({
      competency_id,
      score: score === "" || score == null ? null : Number(score),
      note: "",
    }));
    const dailyAssessments =
      tab === "QUICK_DAILY_FEEDBACK" ? assessmentsFromState(taskState, dailyTasks) : [];
    const customAssessments =
      tab === "QUICK_DAILY_FEEDBACK"
        ? customAssessmentsFromState(customCategoryState, activeCustomCategories)
        : [];
    return {
      learner_id: form.learner_id,
      programme_id: form.programme_id,
      lesson_id: null,
      training_mode: form.training_mode,
      workflow_kind: tab === "QUICK_DAILY_FEEDBACK" ? "QUICK_DAILY_FEEDBACK" : form.workflow_kind,
      score_visibility: form.score_visibility,
      external_share_state: "NOT_SHAREABLE_EXTERNALLY",
      session_date: form.session_date,
      session_time_ampm: form.session_time_ampm,
      location: "",
      duration_minutes: form.confirmed_driving_minutes ? Number(form.confirmed_driving_minutes) : null,
      lesson_focus: "",
      strengths: form.strengths,
      areas_for_improvement: form.areas_for_improvement,
      instructor_notes_private: form.instructor_notes_private,
      learner_visible_notes: "",
      learner_reflection: "",
      skills_practised: [],
      quick_lesson_tasks: tab === "QUICK_LESSON" ? selectedTasks : [],
      skill_ratings: {},
      competency_assessments: assessments,
      daily_task_assessments: dailyAssessments,
      custom_category_assessments: customAssessments,
      hazard_observations: form.hazard_note ? [form.hazard_note] : [],
      readiness_state: form.training_mode === "C" ? form.readiness_state : null,
      recommendations: "",
      next_session_focus: form.next_session_focus,
      attachments: [],
      publication_state: "DRAFT",
    };
  };

  const dailyProgressScore = useMemo(
    () => (tab === "QUICK_DAILY_FEEDBACK" ? computeProgressScore(taskState) : null),
    [tab, taskState]
  );

  const upsertDraft = async ({ explicitSave = false } = {}) => {
    const payload = { ...buildPayload(), explicit_save: explicitSave };
    if (currentRecordId) {
      const res = await api.put(`/instructor/training-records/${currentRecordId}`, payload);
      return res.data.record;
    }
    const res = await api.post("/instructor/training-records", payload);
    const record = res.data.record;
    setCurrentRecordId(record.id);
    sessionStorage.setItem(`qdf-draft-${payload.learner_id}-${payload.session_date}`, record.id);
    return record;
  };

  const formHasMeaningfulDraft = useCallback(
    () =>
      qdfFormHasMeaningfulContent({
        form,
        taskState,
        customCategoryState,
        dailyTasks,
        activeCustomCategories,
        selectedTasks,
        tab,
      }),
    [form, taskState, customCategoryState, dailyTasks, activeCustomCategories, selectedTasks, tab]
  );

  const autosaveDraft = useCallback(async () => {
    if (tab !== "QUICK_DAILY_FEEDBACK" || !form.learner_id || !form.programme_id) return;
    if (!isPracticalTrainingEnabled(selectedLearner)) return;
    if (draftRestorePendingRef.current) return;
    if (!formDirty || !formHasMeaningfulDraft()) return;
    setAutosaveStatus("saving");
    try {
      const record = await draftSaveCoordinatorRef.current.runSave(() => upsertDraft({ explicitSave: false }));
      await linkPendingSketches(record.id);
      setAutosaveStatus("saved");
      setFormDirty(false);
      setError("");
    } catch (err) {
      setAutosaveStatus("retry");
      setError(formatApiError(err) || "Unable to save — retrying.");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- upsertDraft/linkPendingSketches; full deps retrigger autosave loops
  }, [tab, form, selectedLearner, currentRecordId, taskState, customCategoryState, activeCustomCategories, selectedSkills, selectedTasks, dailyTasks, formDirty, formHasMeaningfulDraft]);

  useEffect(() => {
    if (tab !== "QUICK_DAILY_FEEDBACK" || !isPracticalTrainingEnabled(selectedLearner)) {
      setAutosaveStatus("idle");
      return undefined;
    }
    if (draftRestorePendingRef.current || !formDirty || !formHasMeaningfulDraft()) {
      return undefined;
    }
    clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(() => {
      autosaveDraft();
    }, 1500);
    return () => clearTimeout(autosaveTimerRef.current);
  }, [
    tab,
    taskState,
    form.strengths,
    form.areas_for_improvement,
    form.next_session_focus,
    form.score_visibility,
    form.instructor_notes_private,
    customCategoryState,
    selectedLearner,
    autosaveDraft,
    formDirty,
    formHasMeaningfulDraft,
  ]);

  const estimatedSeconds = useMemo(() => {
    if (timingLog.length < 2) return null;
    const start = timingLog[0].at;
    const end = timingLog[timingLog.length - 1].at;
    return Math.round((end - start) / 1000);
  }, [timingLog]);

  const linkPendingSketches = async (recordId) => {
    if (!pendingSketchIds.length) return;
    const linked = [];
    for (const sketchId of pendingSketchIds) {
      try {
        const res = await api.put(`/instructor/training-sketches/${sketchId}`, { training_record_id: recordId });
        linked.push(res.data.sketch);
      } catch {
        // Best-effort link; sketch remains available to link again on next save.
      }
    }
    if (linked.length) {
      setSketches((prev) => prev.map((s) => linked.find((l) => l.sketch_id === s.sketch_id) || s));
      setPendingSketchIds((prev) => prev.filter((id) => !linked.some((l) => l.sketch_id === id)));
    }
  };

  const saveDraft = async () => {
    setMessage("");
    setError("");
    if (!isInstructorSession(user)) {
      setError(
        "Your session is not signed in as an instructor. Log out, sign in again as your instructor account, and keep learner tests in a separate browser."
      );
      setAutosaveStatus("retry");
      return null;
    }
    if (!form.learner_id || !form.programme_id) {
      setError("Select an assigned learner before saving.");
      return null;
    }
    if (!learnerOnWorkingRoster(form.learner_id) || selectedLearner?.id !== form.learner_id) {
      setError(
        "This learner is not on the selected instructor roster. Re-select the instructor and learner before saving."
      );
      return null;
    }
    if (!isPracticalTrainingEnabled(selectedLearner)) {
      setError("Practical training is not enabled for this learner.");
      return null;
    }
    setBusy(true);
    try {
      const record = await draftSaveCoordinatorRef.current.runSave(() =>
        upsertDraft({ explicitSave: true })
      );
      await linkPendingSketches(record.id);
      await loadSketchesForRecord(record.id);
      setMessage(`Training draft saved (${record.id.slice(0, 8)}…). Learner cannot see drafts.`);
      setAutosaveStatus("saved");
      setFormDirty(false);
      setTimingLog((t) => [...t, { step: "save_draft", at: Date.now() }]);
      await loadHistory(form.learner_id, form.programme_id);
      return record;
    } catch (err) {
      setError(formatApiError(err) || "Could not save draft.");
      setAutosaveStatus("retry");
      return null;
    } finally {
      setBusy(false);
    }
  };

  const saveAndPublish = async () => {
    if (publishGuardRef.current) return;
    publishGuardRef.current = true;
    setMessage("");
    setError("");
    if (!isInstructorSession(user)) {
      setError(
        "Your session is not signed in as an instructor. Log out, sign in again as your instructor account, and keep learner tests in a separate browser."
      );
      publishGuardRef.current = false;
      return;
    }
    let record = null;
    if (!formDirty && autosaveStatus === "saved" && currentRecordId) {
      try {
        const res = await api.get(`/instructor/training-records/${currentRecordId}`);
        record = res.data.record;
      } catch (err) {
        setError(formatApiError(err) || "Could not load the saved draft before publish.");
        setAutosaveStatus("retry");
        publishGuardRef.current = false;
        return;
      }
    } else {
      record = await saveDraft();
    }
    if (!record) {
      publishGuardRef.current = false;
      return;
    }
    setBusy(true);
    let hoursCreditedForSession = Boolean(record.practice_hours_entry_id);
    try {
      const driveMins = Number(form.confirmed_driving_minutes);
      if (driveMins >= 1 && driveMins <= 600 && !record.practice_hours_entry_id) {
        const cr = await api.post(`/instructor/training-records/${record.id}/complete-session`, {
          confirmed_driving_minutes: driveMins,
          change_reason: "Instructor confirmed driving time for this lesson",
        });
        hoursCreditedForSession = Boolean(cr.data?.record?.practice_hours_entry_id || cr.data?.practice_hours?.entry?.id);
      }
      await api.post(`/instructor/training-records/${record.id}/approve`, { change_reason: "instructor approve publish" });
      await api.post(`/instructor/training-records/${record.id}/publish`, { change_reason: "instructor approve publish" });
      setMessage(`Published (${record.id.slice(0, 8)}…). Learner can see authorised feedback.`);
      setTimingLog((t) => [...t, { step: "publish", at: Date.now() }]);
      if (makeLatestSketchVisible) {
        const recordSketches = sketches.filter((s) => s.training_record_id === record.id);
        const latest =
          (inlineSketchId && recordSketches.find((s) => s.sketch_id === inlineSketchId)) ||
          recordSketches[recordSketches.length - 1] ||
          (inlineSketchId && sketches.find((s) => s.sketch_id === inlineSketchId));
        if (latest) {
          try {
            const visRes = await api.post(`/instructor/training-sketches/${latest.sketch_id}/set-visibility`, {
              visibility: "LEARNER_VISIBLE",
            });
            setSketches((prev) => prev.map((s) => (s.sketch_id === latest.sketch_id ? visRes.data.sketch : s)));
          } catch {
            setMessage((m) => `${m} (Sketch could not be made learner-visible.)`);
          }
        }
      }
      await loadHistory(form.learner_id, form.programme_id);
      setTimelineRefreshKey((k) => k + 1);
    } catch (err) {
      const detail = formatApiError(err) || "Could not publish record.";
      if (hoursCreditedForSession) {
        setMessage(
          `Driving time for this session is already credited toward the GDI practice target. Publish failed — use Approve & Publish again (minutes will not be double-counted).`
        );
      }
      setError(detail);
    } finally {
      setBusy(false);
      publishGuardRef.current = false;
    }
  };

  const openSketchModal = (sketch = null) => {
    setSketchViewReadOnly(false);
    setEditingSketch(sketch);
    setSketchError("");
    setSketchModalOpen(true);
  };

  const openTimelineSketch = (sketch) => {
    if (!sketch || sketch.access !== "full") return;
    setEditingSketch({
      sketch_id: sketch.sketch_id,
      title: sketch.title || "Training sketch",
      description: "",
      template_id: sketch.template_id || "blank",
      pages: sketch.pages || [],
      strokes_json: sketch.pages?.[0]?.strokes_json || "",
    });
    setSketchViewReadOnly(true);
    setSketchError("");
    setSketchModalOpen(true);
  };

  const closeSketchModal = () => {
    setSketchModalOpen(false);
    setEditingSketch(null);
    setSketchViewReadOnly(false);
  };

  const handleSketchSave = async ({ title, description, template_id, strokes_json, pages }) => {
    if (!form.learner_id || !form.programme_id) {
      setSketchError("Select an assigned learner before adding a sketch.");
      return;
    }
    if (!isPracticalTrainingEnabled(selectedLearner)) {
      setSketchError("Practical training is not enabled for this learner.");
      return;
    }
    setSketchSaving(true);
    setSketchError("");
    try {
      if (editingSketch) {
        const res = await api.put(`/instructor/training-sketches/${editingSketch.sketch_id}`, {
          title,
          description,
          template_id,
          strokes_json,
          pages,
        });
        setSketches((prev) => prev.map((s) => (s.sketch_id === res.data.sketch.sketch_id ? res.data.sketch : s)));
      } else {
        const res = await api.post("/instructor/training-sketches", {
          learner_id: form.learner_id,
          programme_id: form.programme_id,
          training_record_id: currentRecordId || null,
          pending_client_id: currentRecordId ? null : pendingClientId(),
          template_id,
          strokes_json,
          pages,
          title,
          description,
          visibility: "PRIVATE_INSTRUCTOR",
        });
        const saved = res.data.sketch;
        setSketches((prev) => [...prev, saved]);
        if (!saved.training_record_id) {
          setPendingSketchIds((prev) => [...prev, saved.sketch_id]);
        }
      }
      setSketchModalOpen(false);
      setEditingSketch(null);
    } catch (err) {
      setSketchError(err?.response?.data?.detail || "Could not save sketch.");
    } finally {
      setSketchSaving(false);
    }
  };

  const generateAiDraft = async () => {
    setError("");
    if (!isPracticalTrainingEnabled(selectedLearner)) {
      setError("Practical training is not enabled for this learner.");
      return;
    }
    setBusy(true);
    try {
      const payload = buildPayload();
      const res = await api.post("/instructor/ai-draft", {
        learner_id: payload.learner_id,
        programme_id: payload.programme_id,
        strengths: payload.strengths,
        areas_for_improvement: payload.areas_for_improvement,
        next_session_focus: payload.next_session_focus,
        quick_lesson_tasks: payload.quick_lesson_tasks,
        competency_assessments: payload.competency_assessments,
        daily_task_assessments: payload.daily_task_assessments,
      });
      setAiDraft(res.data);
      const wentWell = res.data.went_well || "";
      const needsWork = res.data.needs_work || "";
      const nextLesson = res.data.next_lesson || "";
      if (wentWell || needsWork || nextLesson) {
        setForm((f) => ({
          ...f,
          strengths: wentWell || f.strengths,
          areas_for_improvement: needsWork || f.areas_for_improvement,
          next_session_focus: nextLesson || f.next_session_focus,
        }));
      }
      setMessage(
        `AI draft ready for review (${res.data.status}). Not published. ${res.data.runtime === "PASS" ? "Provider connected." : "Template draft from your scores — review before publishing."}`
      );
      setError("");
    } catch (err) {
      setError(formatApiError(err) || "AI feedback could not be generated. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const transition = async (recordId, action) => {
    setMessage("");
    setError("");
    setBusy(true);
    try {
      await api.post(`/instructor/training-records/${recordId}/${action}`, { change_reason: "" });
      setMessage(`Record ${action}d.`);
      await loadHistory(form.learner_id, form.programme_id);
      setTimelineRefreshKey((k) => k + 1);
    } catch (err) {
      setError(err?.response?.data?.detail || `Could not ${action} record.`);
    } finally {
      setBusy(false);
    }
  };

  const taskLabel = (taskId) => {
    for (const cat of quickCategories) {
      const hit = (cat.tasks || []).find((t) => t.task_id === taskId);
      if (hit) return hit.label;
    }
    return QUICK_TASK_LABELS[taskId] || taskId;
  };

  const practicalEnabled = isPracticalTrainingEnabled(selectedLearner);

  const focusLabels = useMemo(
    () => learnerProgression?.session_focus?.suggested_focus_today || [],
    [learnerProgression]
  );

  const handleQdfSketchSave = async (payload) => {
    const { title = "", description = "", template_id, strokes_json, pages } = payload || {};
    if (!form.learner_id || !form.programme_id) {
      setSketchError("Select an assigned learner before adding a sketch.");
      return;
    }
    if (!practicalEnabled) {
      setSketchError("Practical training is not enabled for this learner.");
      return;
    }
    const strokesBytes = strokes_json ? strokes_json.length : 0;
    const pageCount = Array.isArray(pages) ? pages.length : 1;
    setSketchSaving(true);
    setSketchError("");
    setSketchSaveRevision(null);
    try {
      const recordId = currentRecordId || null;
      let saved;
      if (inlineSketchId) {
        const res = await api.put(`/instructor/training-sketches/${inlineSketchId}`, {
          title: title || inlineSketch?.title || "Daily feedback sketch",
          description,
          template_id: template_id || "blank",
          strokes_json,
          pages,
          training_record_id: recordId,
          change_reason: "Inline sketchbook save",
          expected_revision: inlineSketch?.revision ?? undefined,
        });
        saved = res.data.sketch;
        setSketches((prev) => prev.map((s) => (s.sketch_id === saved.sketch_id ? saved : s)));
      } else {
        const res = await api.post("/instructor/training-sketches", {
          learner_id: form.learner_id,
          programme_id: form.programme_id,
          training_record_id: recordId,
          template_id: template_id || "blank",
          strokes_json,
          pages,
          title: title || "Daily feedback sketch",
          description,
          visibility: "PRIVATE_INSTRUCTOR",
        });
        saved = res.data.sketch;
        setSketches((prev) => [...prev, saved]);
        setInlineSketchId(saved.sketch_id);
        if (!recordId) {
          setPendingSketchIds((prev) => (prev.includes(saved.sketch_id) ? prev : [...prev, saved.sketch_id]));
        }
      }
      if (recordId) {
        await loadSketchesForRecord(recordId);
      } else {
        await loadLearnerSketches(form.learner_id, form.programme_id);
      }
      setSketchSaveRevision(saved.revision ?? null);
      if (process.env.NODE_ENV !== "production") {
        console.info("[sketch-save] ok", {
          sketch_id: saved.sketch_id,
          revision: saved.revision,
          strokes_bytes: strokesBytes,
          page_count: pageCount,
        });
      }
      setTimelineRefreshKey((k) => k + 1);
    } catch (err) {
      setSketchError(formatApiError(err) || "Sketch could not be saved.");
    } finally {
      setSketchSaving(false);
    }
  };

  const qdfSharedProps = {
    tasks: dailyTasks,
    scoreLabels: dailyScoreLabels,
    taskState,
    setTaskState: handleTaskStateChange,
    customCategories: activeCustomCategories,
    customCategoryState,
    setCustomCategoryState: handleCustomCategoryStateChange,
    form,
    onChange,
    hasLearner: !!selectedLearner,
    practicalEnabled,
    autosaveStatus,
    onManualSave: saveDraft,
    onGenerateFeedback: generateAiDraft,
    onApprovePublish: saveAndPublish,
    busy,
    dailyProgressScore,
    onSketchSave: handleQdfSketchSave,
    sketchSaving,
    sketchError,
    sketches,
    inlineSketchPages: inlineSketch?.pages || null,
    inlineSketchStrokesJson: inlineSketch?.strokes_json || "",
    inlineSketchKey: inlineSketchId || "new",
    sketchSaveRevision,
    onEditSketch: openSketchModal,
    aiDraft,
    makeLatestSketchVisible,
    onMakeLatestSketchVisible: setMakeLatestSketchVisible,
    variant: "audit",
    focusLabels,
  };

  return (
    <div className="instructor-audit-page" data-testid="instructor-dashboard">
      {isAdminViewer && (
        <div className="audit-surface p-4 mb-3 space-y-2" data-testid="admin-instructor-roster-gate">
          <p className="text-sm font-medium">Admin: authorized instructor roster view</p>
          <p className="text-sm text-muted-foreground">
            Select an instructor to load their assignment roster (Operational tabs). For all learners use{" "}
            <Link to="/admin/learners" className="underline">Learner oversight</Link>.
          </p>
          <select
            className="input max-w-md"
            data-testid="admin-as-instructor-select"
            value={asInstructorId}
            onChange={(e) => {
              const id = e.target.value;
              setSearchParams(
                (current) => {
                  const next = new URLSearchParams(current);
                  next.delete("learner_id");
                  if (id) next.set("as_instructor_id", id);
                  else next.delete("as_instructor_id");
                  return next;
                },
                { replace: true }
              );
            }}
          >
            <option value="">— Select instructor —</option>
            {adminInstructors.map((i) => (
              <option key={i.id} value={i.id}>{i.display_name || i.email}</option>
            ))}
          </select>
        </div>
      )}
      <InstructorLearnerHeader
        learner={selectedLearner}
        programmeLabel={programmeLabel(form.programme_id)}
        sessionDate={form.session_date}
        sessionTimeAmPm={form.session_time_ampm}
        pickerOpen={pickerOpen}
        onTogglePicker={() => setPickerOpen((v) => !v)}
        onSwitchClick={() => setPickerOpen((v) => !v)}
      />

      <InstructorLearnerPicker
        open={pickerOpen}
        learners={learners}
        selectedId={selectedLearner?.id}
        onSelect={requestSelectLearner}
        qaDetailsRef={qaDetailsRef}
        totalAssigned={learnersStatus === "ready" ? learners.length : null}
        rosterListCounts={rosterListCounts}
        operationalLifecycleTabCounts={operationalLifecycleTabCounts}
        operationalMirrorScheduler={operationalMirrorScheduler}
        loading={learnersStatus === "idle" || learnersStatus === "loading"}
        loadError={learnersStatus === "error" ? learnersError : ""}
        onRetry={loadLearners}
        onArchiveLearner={archiveLearnerOnRoster}
        onRestoreLearner={restoreLearnerOnRoster}
        rosterActionBusy={rosterActionBusy}
      />

      {(learnersStatus === "idle" || learnersStatus === "loading") && !selectedLearner && (
        <div className="audit-surface mt-3 p-4 text-sm" data-testid="instructor-learners-loading">
          Loading assigned learners…
        </div>
      )}

      {learnersStatus === "error" && !selectedLearner && !gatewayError && (
        <div className="audit-surface mt-3 p-4 text-sm space-y-3" role="alert" data-testid="instructor-learners-error">
          <p>{learnersError || "Could not load learners. Please try again."}</p>
          <button type="button" className="audit-btn-outline-dark" onClick={loadLearners} data-testid="instructor-learners-retry">
            Retry
          </button>
        </div>
      )}

      {gatewayError && (
        <div className="audit-restricted-banner mt-3" role="alert" data-testid="gateway-launch-instructor-error">
          {gatewayError}
        </div>
      )}

      {!selectedLearner && learnersStatus === "ready" && learners.length === 0 && !gatewayError && (
        <div className="audit-surface mt-3 p-4 text-sm" data-testid="instructor-no-assignments-banner">
          {isAdminViewer && !asInstructorId
            ? "Choose an instructor above to view their roster, or open Learner oversight for the global learner list."
            : "No learners are assigned to your Academy instructor account. Open training from GDI on an assigned student, or ask an administrator to assign training access."}
        </div>
      )}

      {selectedLearner && !practicalEnabled && (
        <div className="audit-restricted-banner mt-3" data-testid="tr-training-not-enabled">
          Practical training is not enabled for this learner. Theory learning remains available. Save Draft, Approve
          &amp; Publish, sketches, and practical feedback stay disabled until training is authorised.
        </div>
      )}

      {selectedLearner && (
        <InstructorAdvancedAccess
          learnerId={selectedLearner.id}
          programmeId={form.programme_id}
          assignments={assignments}
          onChanged={() => loadLearners()}
        />
      )}

      {selectedLearner && (
        <InstructorPracticalTrainingWrites learnerId={selectedLearner.id} programmeId={form.programme_id} />
      )}

      {selectedLearner && (
        <InstructorTrainingOutcome learnerId={selectedLearner.id} programmeId={form.programme_id} />
      )}

      {selectedLearner && (
        <InstructorPracticeHoursPanel learnerId={selectedLearner.id} programmeId={form.programme_id} />
      )}

      <InstructorModeTabs tab={tab} onChange={setWorkflowTab} />

      <section data-testid="training-record-form" ref={formSectionRef}>
        {selectedLearner && (
          <details className="audit-surface audit-section mt-3" data-testid="instructor-learning-preferences-card">
            <summary className="audit-section-title cursor-pointer min-h-11 flex items-center">
              Learning preferences
            </summary>
            <div className="mt-2 space-y-2">
              <p className="text-xs text-muted-foreground">
                Coaching tip: lead with confidence — acknowledge what went well before what to adjust.
              </p>
              {learnerPreferences?.selections?.length > 0 ? (
                <ul className="text-sm list-disc list-inside space-y-0.5" data-testid="instructor-preferences-list">
                  {learnerPreferences.selection_labels.map((label, i) => (
                    <li key={learnerPreferences.selections[i]}>{label}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No learning preferences recorded yet.
                </p>
              )}
              {learnerPreferences?.other_notes && (
                <p className="text-sm" data-testid="instructor-preferences-notes">
                  Notes: {learnerPreferences.other_notes}
                </p>
              )}
            </div>
          </details>
        )}

        {tab === "QUICK_DAILY_FEEDBACK" && (
          <div className="audit-workspace-grid split">
            <div className="space-y-3">
              <InstructorCustomCategoriesPanel
                categories={customCategories}
                onCategoriesChange={handleCategoriesChange}
              />
              {selectedLearner && <InstructorFocusToday progression={learnerProgression} />}
              {selectedLearner && <PerformanceDashboardPanel progression={learnerProgression} />}
              <QuickDailyFeedbackPanel {...qdfSharedProps} part="assessment" />
              <QuickDailyFeedbackPanel {...qdfSharedProps} part="feedback" />
              <TrainingTimeline
                key={timelineRefreshKey}
                learnerId={selectedLearner?.id}
                programmeId={form.programme_id}
                api={api}
                busy={busy}
                practicalEnabled={practicalEnabled}
                onTransition={transition}
                onViewSketch={openTimelineSketch}
              />
            </div>
            <QuickDailyFeedbackPanel {...qdfSharedProps} part="sketch" />
          </div>
        )}

        {tab !== "QUICK_DAILY_FEEDBACK" && (
          <div className="py-3 space-y-4">
            {selectedLearner && learnerProgression && (
              <>
                <InstructorFocusToday progression={learnerProgression} />
                <PerformanceDashboardPanel progression={learnerProgression} />
              </>
            )}

            <div className="audit-surface audit-section" data-testid="training-sketches-panel">
              <div className="audit-panel-header">
                <h3 className="audit-section-title">Driving sketches</h3>
                <button
                  type="button"
                  className="audit-btn-compact"
                  onClick={() => openSketchModal(null)}
                  disabled={!selectedLearner || !practicalEnabled}
                  data-testid="tr-add-sketch"
                >
                  Add sketch
                </button>
              </div>
              {sketches.length > 0 ? (
                <ul className="space-y-1 text-sm" data-testid="training-sketches-list">
                  {sketches.map((s) => (
                    <li key={s.sketch_id} className="flex flex-wrap items-center justify-between gap-2">
                      <span>
                        {s.title || "Untitled sketch"} · {SKETCH_TEMPLATE_LABELS[s.template_id] || s.template_id} ·{" "}
                        {s.visibility === "LEARNER_VISIBLE" ? "Learner-visible" : "Private"}
                      </span>
                      <button
                        type="button"
                        className="audit-btn-compact"
                        style={{ marginLeft: 0 }}
                        onClick={() => openSketchModal(s)}
                        data-testid={`edit-sketch-${s.sketch_id}`}
                      >
                        Edit
                      </button>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-sm text-muted-foreground">
                  No sketches added to this session yet.
                </p>
              )}
              {sketchesTruncated && (
                <button
                  type="button"
                  className="audit-btn-compact mt-2"
                  onClick={loadMoreLearnerSketches}
                  disabled={sketchesLoadingMore || !practicalEnabled}
                  data-testid="training-sketches-load-more"
                >
                  {sketchesLoadingMore ? "Loading…" : "Load older sketches"}
                </button>
              )}
              {sketchError && (
                <p role="alert" className="text-destructive text-sm mt-2" data-testid="tr-sketch-error">
                  {sketchError}
                </p>
              )}
            </div>

            {tab === "QUICK_LESSON" && (
              <div className="audit-surface audit-section space-y-4" data-testid="quick-lesson-panel">
                <div className="flex flex-wrap gap-2" data-testid="quick-picks">
                  <span className="text-sm font-medium w-full">Quick picks</span>
                  {QUICK_PICKS.map((id) => (
                    <TaskVisualCard
                      key={id}
                      taskId={id}
                      label={taskLabel(id)}
                      selected={selectedTasks.includes(id)}
                      onToggle={toggleTask}
                      testId={`quick-pick-${id}`}
                    />
                  ))}
                </div>
                {quickCategories.map((cat) => (
                  <div key={cat.category_id} className="space-y-2">
                    <h3 className="text-sm font-semibold uppercase tracking-wide">{cat.name}</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {(cat.tasks || []).map((t) => (
                        <TaskVisualCard
                          key={t.task_id}
                          taskId={t.task_id}
                          label={t.label}
                          selected={selectedTasks.includes(t.task_id)}
                          onToggle={toggleTask}
                          testId={`ql-task-${t.task_id}`}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {tab === "DETAILED_ASSESSMENT" && (
              <div className="audit-surface audit-section space-y-3" data-testid="detailed-panel">
                <p className="text-sm text-muted-foreground">
                  Optional 1–5 scoring. Unscored is allowed. Score visibility defaults to hidden.
                </p>
                <DetailedCriteriaPanel
                  categories={detailedCategories}
                  selectedSkills={selectedSkills}
                  onToggleSkill={toggleSkill}
                  onSetSkillScore={setSkillScore}
                  openDetailed={openDetailed}
                  onToggleCategory={(categoryId, open) => setOpenDetailed((o) => ({ ...o, [categoryId]: open }))}
                  testIdPrefix="det"
                  disabled={!practicalEnabled}
                />
                <label className="block space-y-1">
                  <span>Score visibility to learner</span>
                  <select
                    className="w-full border rounded px-3 py-2 min-h-11"
                    name="score_visibility"
                    value={form.score_visibility}
                    onChange={onChange}
                    data-testid="tr-score-vis"
                    disabled={!practicalEnabled}
                  >
                    <option value="HIDDEN">Hidden (default safe)</option>
                    <option value="VISIBLE">Visible</option>
                  </select>
                </label>
              </div>
            )}

            {tab === "MOCK_READINESS" && (
              <div className="audit-surface audit-section space-y-3" data-testid="mock-panel">
                <label className="block space-y-1">
                  <span>Readiness</span>
                  <select
                    className="w-full border rounded px-3 py-2 min-h-11"
                    name="readiness_state"
                    value={form.readiness_state}
                    onChange={onChange}
                    data-testid="tr-readiness"
                    disabled={!practicalEnabled}
                  >
                    <option value="NOT_READY">Not Ready</option>
                    <option value="DEVELOPING">Developing</option>
                    <option value="NEARLY_READY">Nearly Ready</option>
                    <option value="READY_FOR_MOCK">Ready for Mock</option>
                    <option value="TEST_READY">Test Ready</option>
                  </select>
                  <span className="text-xs text-muted-foreground">
                    Educational readiness only — no NZTA pass guarantee, no percentage.
                  </span>
                </label>
                <label className="block space-y-1">
                  <span>Hazard observation (optional)</span>
                  <input
                    className="w-full border rounded px-3 py-2 min-h-11"
                    name="hazard_note"
                    value={form.hazard_note}
                    onChange={onChange}
                    data-testid="tr-hazard"
                    disabled={!practicalEnabled}
                  />
                </label>
                <DetailedCriteriaPanel
                  categories={detailedCategories}
                  selectedSkills={selectedSkills}
                  onToggleSkill={toggleSkill}
                  onSetSkillScore={setSkillScore}
                  openDetailed={openDetailed}
                  onToggleCategory={(categoryId, open) => setOpenDetailed((o) => ({ ...o, [categoryId]: open }))}
                  testIdPrefix="mock"
                  disabled={!practicalEnabled}
                />
                <label className="block space-y-1">
                  <span>Score visibility</span>
                  <select
                    className="w-full border rounded px-3 py-2 min-h-11"
                    name="score_visibility"
                    value={form.score_visibility}
                    onChange={onChange}
                    data-testid="tr-score-vis-mock"
                    disabled={!practicalEnabled}
                  >
                    <option value="HIDDEN">Hidden</option>
                    <option value="VISIBLE">Visible</option>
                  </select>
                </label>
              </div>
            )}

            <QuickDailyFeedbackPanel {...qdfSharedProps} part="feedback" />

            {aiDraft && (
              <div className="audit-surface audit-section text-sm" data-testid="ai-draft-box">
                <p className="font-medium">AI Draft ({aiDraft.status})</p>
                <p className="text-sm">{aiDraft.draft_text}</p>
              </div>
            )}

            {sketches.length > 0 && (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={makeLatestSketchVisible}
                  onChange={(e) => setMakeLatestSketchVisible(e.target.checked)}
                  disabled={!practicalEnabled}
                  data-testid="tr-sketch-learner-visible"
                />
                <span>Make latest sketch learner-visible when published (default off)</span>
              </label>
            )}

            <TrainingTimeline
              key={timelineRefreshKey}
              learnerId={selectedLearner?.id}
              programmeId={form.programme_id}
              api={api}
              busy={busy}
              practicalEnabled={practicalEnabled}
              onTransition={transition}
              onViewSketch={openTimelineSketch}
            />

            {estimatedSeconds != null && (
              <p className="text-xs text-muted-foreground" data-testid="timing-estimate">
                Session interaction span ≈ {estimatedSeconds}s · steps {timingLog.length}
              </p>
            )}
          </div>
        )}

        {(message || error) && (
          <div className="py-2 space-y-1">
            {message && (
              <p role="status" data-testid="tr-message">
                {message}
              </p>
            )}
            {error && (
              <p role="alert" className="text-destructive" data-testid="tr-error">
                {error}
              </p>
            )}
          </div>
        )}
      </section>

      {selectedLearner && (
        <>
          <section className="card space-y-2 mt-3" data-testid="lesson-driving-minutes">
            <h3 className="text-sm font-semibold">Driving time for 60-hour GDI practice target</h3>
            <p className="text-xs text-muted-foreground">
              Confirm actual driving minutes for this lesson to credit one instructor-confirmed practice entry when you
              approve &amp; publish. Publishing alone does not add hours without confirmed minutes here.
            </p>
            <label className="text-sm block">
              Confirmed driving minutes (optional)
              <input
                className="input mt-1 w-32"
                type="number"
                min={1}
                max={600}
                name="confirmed_driving_minutes"
                value={form.confirmed_driving_minutes}
                onChange={onChange}
                disabled={!practicalEnabled || busy}
                data-testid="confirmed-driving-minutes"
              />
            </label>
          </section>
          <InstructorActionBar
            autosaveStatus={autosaveStatus}
            formDirty={formDirty}
            practicalEnabled={practicalEnabled}
            busy={busy}
            onSaveDraft={saveDraft}
            onApprovePublish={saveAndPublish}
            showGenerate={tab !== "QUICK_DAILY_FEEDBACK"}
            onGenerate={generateAiDraft}
          />
        </>
      )}

      <SketchModal
        open={sketchModalOpen}
        onClose={closeSketchModal}
        onSave={handleSketchSave}
        saving={sketchSaving}
        error={sketchError}
        readOnly={sketchViewReadOnly}
        initialTitle={editingSketch?.title || ""}
        initialDescription={editingSketch?.description || ""}
        initialTemplateId={editingSketch?.template_id || "blank"}
        initialPages={editingSketch?.pages || null}
        initialStrokesJson={editingSketch?.strokes_json || ""}
        heading={editingSketch ? "Edit driving sketch" : "New driving sketch"}
      />
    </div>
  );
}

